/**
 * SnowOverlay.tsx — the "Snow" overlay (owner-directed secret feature).
 *
 * Trigger: the game CREATOR adds the game's own invite code to their
 * wishlist (see ManageWishlistOverlay). The code is NEVER added to the
 * wishlist (the server rejects it too); instead this overlay opens and
 * fetches the full pairing map from GET /games/:id/snow.
 *
 * Secrecy (locked): the pairing data is fetched ONLY when this overlay is
 * opened — the GamePage never preloads it, and no other endpoint carries
 * other players' assignments. The server is the final authority: creator
 * only, Started/Ended only, rate limited. Assignments are immutable once
 * the game has started, so each open performs exactly one fetch and
 * closing discards the data (kept in no page-level state).
 * Dialog behavior (focus trap, Escape, focus return, ARIA) comes from the
 * shared K01 Modal.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiClientError, type SnowPairsResponseData } from '@shared';
import { apiV1 } from '../api';
import { Modal } from './Modal';

interface SnowOverlayProps {
  gameId: number;
  onClose: () => void;
}

export function SnowOverlay({ gameId, onClose }: SnowOverlayProps) {
  const [pairs, setPairs] = useState<SnowPairsResponseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetched ONLY when the overlay is opened (component mount).
  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      setPairs(await apiV1.snowPairs(gameId));
    } catch (err) {
      setLoadError(
        err instanceof ApiClientError ? err.message : 'Could not load the pairings.',
      );
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Modal title="Snow" onClose={onClose} blocked={loading}>
      <p className="overlay-status">
        The secret pairings — visible only to the creator.
      </p>

      {loading && pairs === null && (
        <p className="overlay-status" aria-live="polite">
          Loading…
        </p>
      )}

      {loadError !== null && pairs === null && (
        <p className="auth-error" role="alert">
          {loadError}
        </p>
      )}

      {pairs !== null && (
        <ul className="snow-pairs">
          {pairs.pairs.map((pair) => (
            <li key={`${pair.giver.id}-${pair.receiver.id}`} className="snow-pair">
              <span className="snow-pair-giver">{pair.giver.username}</span>
              <span className="snow-pair-arrow" aria-hidden="true">
                →
              </span>
              <span className="snow-pair-receiver">{pair.receiver.username}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="overlay-actions">
        <button type="button" className="game-refresh" onClick={onClose} disabled={loading}>
          Close
        </button>
      </div>
    </Modal>
  );
}
